import { jsx, jsxs } from "react/jsx-runtime";
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import _ from "lodash";
import LoginPage from "./LoginPage";
import { actions as loginActions } from "./login/loginActions";
import { actions as appActions } from "./appActions";
import Terminal from "./Terminal";
import VtDisabledNotice from "./self/VtDisabledNotice";
import "./Dashboard.scss";
const NEXIO_LOGO_URL = "/nexio_logo.svg";
export function Dashboard({
  isLoggedIn,
  isSelfLoaded,
  isEnabledForVTerm,
  username,
  simpleLoginKey,
  refresh,
  simpleLogin,
  logout,
  paymentMethod,
  isSaveCard,
  vaultedCard,
  vaultedEcheck,
  cssFile,
  isActionCompleted,
  actionResultMessage,
  actionResultStatus,
  actionResultData,
  merchantId,
  resetAction
}) {
  const pageTitle = "Nexio Virtual Terminal";
  const isInIframe = parent !== window;
  const resultTitle = actionResultStatus === "success" ? "Payment was successful!" : "Payment failed";
  const resultMessage = actionResultStatus !== "success" ? actionResultMessage || "" : "";
  useEffect(() => {
    const previousTitle = document.title;
    document.title = pageTitle;
    return () => {
      document.title = previousTitle;
    };
  }, []);
  useEffect(() => {
    if (simpleLoginKey) {
      simpleLogin(simpleLoginKey);
    } else {
      refresh();
    }
  }, [refresh, simpleLogin, simpleLoginKey]);
  const handleLogout = () => {
    logout();
  };
  const handleNewTransaction = () => {
    resetAction();
  };
  if (!isLoggedIn) {
    return /* @__PURE__ */ jsx(LoginPage, { isInIframe, cssFile });
  }
  return /* @__PURE__ */ jsxs("div", { className: "dashboard", children: [
    /* @__PURE__ */ jsxs("header", { className: "dashboard__header", children: [
      /* @__PURE__ */ jsxs("div", { className: "dashboard__title", children: [
        /* @__PURE__ */ jsx(
          "img",
          {
            className: "dashboard__logo",
            src: NEXIO_LOGO_URL,
            alt: "Nexio"
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "dashboard__titleText", children: "Nexio Virtual Terminal" })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "dashboard__headerRight", children: [
        /* @__PURE__ */ jsx("div", { className: "dashboard__email", children: username || "" }),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: handleLogout,
            className: "dashboard__logoutButton",
            children: "Logout"
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsx("main", { className: "dashboard__main", children: !isSelfLoaded ? (
      // Wait for the Virtual Terminal entitlement check (self.isSelfLoaded)
      // to finish before showing the form, so a disabled account is never
      // briefly exposed.
      null
    ) : !isEnabledForVTerm ? (
      // Virtual Terminal access is governed by the "Enable Virtual
      // Terminal" checkbox in Nexio Super. Block the form instead of
      // allowing transactions.
      /* @__PURE__ */ jsx(VtDisabledNotice, {})
    ) : !isActionCompleted ? /* @__PURE__ */ jsx("div", { className: "nexio retailIframe loaded inIframe", children: /* @__PURE__ */ jsx(
      Terminal,
      {
        paymentMethod,
        isSaveCard,
        vaultedCard,
        vaultedEcheck
      }
    ) }) : /* @__PURE__ */ jsxs("section", { className: "dashboard__afterPayment", "aria-live": "polite", children: [
      /* @__PURE__ */ jsxs("div", { className: "dashboard__resultHeader", children: [
        /* @__PURE__ */ jsxs("div", { className: "dashboard__resultText", children: [
          /* @__PURE__ */ jsx("div", { className: "dashboard__resultTitle", children: resultTitle }),
          /* @__PURE__ */ jsx("div", { className: "dashboard__resultMessage", children: resultMessage })
        ] }),
        actionResultStatus === "success" && /* @__PURE__ */ jsx(
          Receipt,
          {
            actionResultData,
            merchantId
          }
        )
      ] }),
      isActionCompleted && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: handleNewTransaction,
          className: "dashboard__newTransactionButton",
          children: "New Transaction"
        }
      )
    ] }) })
  ] });
}
function prettifyDate(value) {
  if (value === null || value === void 0 || value === "") return "";
  const date = typeof value === "number" ? new Date(value) : new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}
function prettifyMoney(amount, currency) {
  if (amount === null || amount === void 0) return "";
  const num = Number(amount);
  if (Number.isNaN(num)) return String(amount);
  const code = currency || "USD";
  try {
    return new Intl.NumberFormat(void 0, { style: "currency", currency: code }).format(num);
  } catch {
    return `${code} ${num.toFixed(2)}`;
  }
}
function formatMerchantId(merchantId) {
  return merchantId !== null && merchantId !== void 0 && String(merchantId).trim() !== "" ? String(merchantId) : "\u2014";
}
function ReceiptRow({ label, value }) {
  if (value === null || value === void 0 || value === "") return null;
  return /* @__PURE__ */ jsxs("div", { className: "dashboard__receiptRow", children: [
    /* @__PURE__ */ jsx("div", { className: "dashboard__receiptLabel", children: label }),
    /* @__PURE__ */ jsx("div", { className: "dashboard__receiptValue", children: value })
  ] });
}
ReceiptRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
function ReceiptSection({ title, items }) {
  const rows = items.filter((item) => item.value !== null && item.value !== void 0 && item.value !== "");
  if (rows.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { className: "dashboard__receiptSection", children: [
    /* @__PURE__ */ jsx("div", { className: "dashboard__receiptSectionTitle", children: title }),
    rows.map((item) => /* @__PURE__ */ jsx(ReceiptRow, { label: item.label, value: item.value }, item.label))
  ] });
}
ReceiptSection.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) })).isRequired
};
function TransactionReceiptSection({ data, merchantId }) {
  const amount = _.get(data, "amount") ?? _.get(data, "data.amount");
  const currency = _.get(data, "currency") ?? _.get(data, "data.currency");
  const transactionDate = _.get(data, "transactionDate") ?? _.get(data, "gatewayResponse.transactionDate");
  const transactionStatus = _.get(data, "transactionStatus") ?? _.get(data, "gatewayResponse.transactionStatus") ?? _.get(data, "isAuthOnly");
  const items = [
    { label: "Merchant:", value: formatMerchantId(merchantId) },
    { label: "Date/Time:", value: prettifyDate(transactionDate) },
    { label: "Transaction Type:", value: transactionStatus === "authOnly" || transactionStatus === true ? "Authorized" : "Sale" },
    { label: "Amount:", value: prettifyMoney(amount, currency) },
    { label: "Currency:", value: currency }
  ];
  return /* @__PURE__ */ jsx(ReceiptSection, { title: "Transaction Receipt", items });
}
TransactionReceiptSection.propTypes = {
  data: PropTypes.object,
  merchantId: PropTypes.string
};
function CardReceiptSection({ data }) {
  const card = _.get(data, "card") ?? _.get(data, "gatewayResponse.card") ?? {};
  const items = [
    { label: "Card Type:", value: _.startCase(_.get(card, "cardType")) },
    { label: "Card Number:", value: _.get(card, "cardNumber") },
    { label: "Card Holder:", value: _.get(card, "cardHolder") },
    { label: "Auth Code:", value: _.get(data, "authCode") ?? _.get(data, "gatewayResponse.authCode") }
  ];
  return /* @__PURE__ */ jsx(ReceiptSection, { title: "Credit Card Information", items });
}
CardReceiptSection.propTypes = {
  data: PropTypes.object
};
function OrderReceiptSection({ data }) {
  const customer = _.get(data, "data.customer") ?? {};
  const items = [
    { label: "Invoice:", value: _.get(customer, "invoice") },
    { label: "Order Number:", value: _.get(customer, "orderNumber") },
    { label: "Description:", value: _.get(data, "data.description") }
  ];
  return /* @__PURE__ */ jsx(ReceiptSection, { title: "Order Information", items });
}
OrderReceiptSection.propTypes = {
  data: PropTypes.object
};
function CustomerReceiptSection({ data }) {
  const customer = _.get(data, "data.customer") ?? {};
  const items = [
    { label: "First Name:", value: _.get(customer, "firstName") },
    { label: "Last Name:", value: _.get(customer, "lastName") },
    { label: "Phone:", value: _.get(customer, "phone") },
    { label: "Email:", value: _.get(customer, "email") },
    { label: "Customer Reference Number:", value: _.get(customer, "customerRef") }
  ];
  return /* @__PURE__ */ jsx(ReceiptSection, { title: "Customer Information", items });
}
CustomerReceiptSection.propTypes = {
  data: PropTypes.object
};
function ReceiptAddressSection({ data, addressPrefix, title }) {
  const customer = _.get(data, "data.customer") ?? {};
  const prefix = addressPrefix === "shipTo" ? "shipTo" : "billTo";
  const lines = [
    _.get(customer, `${prefix}AddressOne`),
    _.get(customer, `${prefix}AddressTwo`),
    [
      _.get(customer, `${prefix}City`),
      _.get(customer, `${prefix}State`),
      _.get(customer, `${prefix}Postal`)
    ].filter(Boolean).join(", "),
    _.get(customer, `${prefix}Country`),
    _.get(customer, `${prefix}Phone`)
  ].filter(Boolean);
  if (lines.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { className: "dashboard__receiptSection", children: [
    /* @__PURE__ */ jsx("div", { className: "dashboard__receiptSectionTitle", children: title }),
    /* @__PURE__ */ jsx("div", { className: "dashboard__receiptAddressBlock", children: lines.join("\n") })
  ] });
}
ReceiptAddressSection.propTypes = {
  data: PropTypes.object,
  addressPrefix: PropTypes.oneOf(["billTo", "shipTo"]).isRequired,
  title: PropTypes.string.isRequired
};
function Receipt({ actionResultData, merchantId }) {
  const handlePrintReceipt = () => {
    window.print();
  };
  const data = actionResultData || {};
  return /* @__PURE__ */ jsxs("div", { className: "dashboard__receipt", children: [
    /* @__PURE__ */ jsxs("div", { className: "dashboard__receiptPrintable", children: [
      /* @__PURE__ */ jsx(TransactionReceiptSection, { data, merchantId }),
      /* @__PURE__ */ jsx(CardReceiptSection, { data }),
      /* @__PURE__ */ jsx(OrderReceiptSection, { data }),
      /* @__PURE__ */ jsx(CustomerReceiptSection, { data }),
      /* @__PURE__ */ jsxs("div", { className: "dashboard__receiptAddresses", children: [
        /* @__PURE__ */ jsx(ReceiptAddressSection, { data, addressPrefix: "billTo", title: "Billing Information" }),
        /* @__PURE__ */ jsx(ReceiptAddressSection, { data, addressPrefix: "shipTo", title: "Shipping Information" })
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "dashboard__receiptActions", children: /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: handlePrintReceipt,
        className: "dashboard__printReceiptButton dashboard__receiptActionButton",
        children: "Print Receipt"
      }
    ) })
  ] });
}
Receipt.propTypes = {
  actionResultData: PropTypes.object,
  merchantId: PropTypes.string
};
Dashboard.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
  isSelfLoaded: PropTypes.bool,
  isEnabledForVTerm: PropTypes.bool,
  username: PropTypes.string,
  simpleLoginKey: PropTypes.string,
  refresh: PropTypes.func.isRequired,
  simpleLogin: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  paymentMethod: PropTypes.string.isRequired,
  isSaveCard: PropTypes.bool,
  vaultedCard: PropTypes.any,
  vaultedEcheck: PropTypes.any,
  cssFile: PropTypes.string,
  isActionCompleted: PropTypes.bool,
  actionResultMessage: PropTypes.string,
  actionResultStatus: PropTypes.oneOf(["success", "error"]),
  actionResultData: PropTypes.object,
  merchantId: PropTypes.string,
  resetAction: PropTypes.func.isRequired
};
export function mapStateToProps({ login, app, customerVault, self }) {
  return {
    isLoggedIn: login.isLoggedIn,
    isSelfLoaded: self.isSelfLoaded,
    isEnabledForVTerm: self.isEnabledForVTerm,
    username: login.username,
    simpleLoginKey: app.simpleLogin,
    paymentMethod: app.paymentMethod,
    isSaveCard: _.get(app, "settings.uiOptions.saveCard"),
    vaultedCard: customerVault.card,
    vaultedEcheck: customerVault.echeck,
    cssFile: _.get(app, "settings.uiOptions.css"),
    isActionCompleted: app.isActionCompleted,
    actionResultMessage: app.actionResultMessage,
    actionResultStatus: app.actionResultStatus,
    actionResultData: app.actionResultData,
    merchantId: app.merchantId
  };
}
export default connect(mapStateToProps, {
  refresh: loginActions.refresh,
  simpleLogin: loginActions.simpleLogin,
  logout: loginActions.logout,
  resetAction: appActions.resetAction
})(Dashboard);
